import { Component, OnDestroy, OnInit, ViewEncapsulation, Input, HostBinding } from '@angular/core';
import { Subscription, of, catchError } from 'rxjs';
import { FieldExpression, NodeExpression, LuceneExpression, toString as luceneToString } from 'lucene';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { Widget, CustomWidget } from '../../../../models/widget';
import { UserService } from '../../../../services/user.service';
import { User } from '../../../../models/user';
import { PaginatedResult } from '../../../../models/paginated-result';
import { Batch } from '../../../../../order/models/batch';
import { pageTransitions } from '../../../../animations/page-transitions';
import { BatchesService } from '../../../../../order/services/batches.service';
import { WEIGHT_OPTIONS, PRODUCT_OPTIONS } from '../../../../models/list';
import { OrderByDirection, OrderBy } from '../../../../utils/constant';
import { NumberPipe } from '../../../../pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../utils/loading-spinner.component';

interface Steelgrade {
	code: string;
	translation: string;
}

interface BatchRstFilter {
	steelgrade: Steelgrade[];
	customerArticleReference: string[];
}

enum FieldContent {
	CUSTOMER_ARTICLE = 'customerArticleReference',
	STEELGRADE = 'steelgrade'

}

enum FieldWeight {
	GROSS = 'qtyBatchGross',
	NET = 'qtyBatchNet'
}

interface RtsData {
	value: number;
	reference: string;
}

const GROUP_STATUS_READY_TO_SHIP = '2';

@Component({
	selector: 'copo-rts-widget',
	templateUrl: './rts-widget.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,

	imports: [NgIf, TranslateModule, MatTooltipModule, NgFor, LoadingSpinnerComponent, NumberPipe]
})
export class RtsWidgetComponent implements OnInit, OnDestroy, CustomWidget {
	@HostBinding('class.widget-content') readonly widgetContent = true;
	@Input() widget!: Widget;
	rtsValues: RtsData[] = [];
	user: User;
	batchesSub?: Subscription;
	weightTypeLabel = '';
	productTypeLabel = '';
	loadingData = true;
	constructor(private readonly batchesService: BatchesService, private readonly userService: UserService) {
		this.user = this.userService.user;
	}

	ngOnInit() {
		const content = <BatchRstFilter> this.widget.content;
		const contentType = <keyof BatchRstFilter> Object.keys(content)[0];
		const contentValues = content[contentType];
		const weightTypeMapped = FieldWeight.GROSS;
		let contentTypeMapped;
		let contentFields;
		if (contentType === 'steelgrade') {
			contentTypeMapped = <const> FieldContent.STEELGRADE;
			contentFields = <const> [contentTypeMapped, 'steelgradeDescription'];
			this.productTypeLabel = PRODUCT_OPTIONS.steelgrade;
		} else {
			contentTypeMapped = <const> FieldContent.CUSTOMER_ARTICLE;
			contentFields = <const> [contentTypeMapped];
			this.productTypeLabel = PRODUCT_OPTIONS.article;
		}
		this.weightTypeLabel = WEIGHT_OPTIONS.gross;
		this.batchesSub = this.batchesService.getBatches(
			[...contentFields, weightTypeMapped],
			this.batchesFilter(contentTypeMapped, contentValues),
			0,
			50,
			new OrderBy(contentTypeMapped, OrderByDirection.DESC),
			true
		).pipe(catchError((err: unknown) => {
			console.error('error retrieving orders', err);
			const emptyBatchArray: Batch[] = [];
			return of({ results: emptyBatchArray, count: 0, page: 1, limit: 1 });
		})).subscribe((batches: PaginatedResult<Batch>) => {
			this.loadingData = false;
			for (const contentValue of contentValues) {
				const value = typeof contentValue !== 'string' && 'code' in contentValue
					? contentValue.code
					: contentValue;
				const index = batches.results.findIndex(batch => {
					const { steelgrade } = batch;
					return value === (steelgrade || batch.customerArticleReference);
				});
				if (index === -1) {
					if (contentType === FieldContent.STEELGRADE) {
						this.rtsValues.push({
							value: 0,
							reference: (<Steelgrade> contentValue).translation
						});
					} else {
						this.rtsValues.push({
							value: 0,
							reference: <string> contentValue
						});
					}
					continue;
				}
				const ref = batches.results[index].steelgradeDescription
					|| batches.results[index].customerArticleReference;
				this.rtsValues.push(
					{
						value: batches.results[index][weightTypeMapped],
						reference: ref
					}
				);
			}
		});
	}

	ngOnDestroy() {
		if (this.batchesSub) {
			this.batchesSub.unsubscribe();
		}
	}

	batchesFilter(contentType: FieldContent, contentValues: Steelgrade[] | string[]) {
		if (contentType === FieldContent.STEELGRADE) {
			return this.batchesSteelgradeFilter(contentType, <Steelgrade[]> contentValues);
		}

		return this.batchesCustomerArticleFilter(contentType, <string[]> contentValues);
	}

	batchesSteelgradeFilter(contentType: string, contentValues: Steelgrade[]) {
		if (contentValues.length === 0) {
			return '';
		}

		let allNode: FieldExpression | NodeExpression = {
			field: 'statusGroup',
			term: GROUP_STATUS_READY_TO_SHIP,
			quoted: true
		};
		let steelgradeCondition: LuceneExpression;
		if (contentValues.length === 1) {
			steelgradeCondition = {
				field: contentType,
				term: contentValues[0].code,
				quoted: true
			};
		} else {
			steelgradeCondition = {
				field: contentType,
				left: {
					field: '<implicit>',
					term: contentValues[0].code,
					quoted: true
				},
				operator: '<implicit>',
				right: {
					field: '<implicit>',
					term: contentValues[1].code,
					quoted: true
				},
				parenthesized: true
			};
			let current = steelgradeCondition;
			for (const steelgrade of contentValues.slice(2)) {
				current.right = {
					left: current.right,
					operator: '<implicit>',
					right: {
						field: '<implicit>',
						term: steelgrade.code,
						quoted: true
					},
					parenthesized: false
				};
				current = current.right;
			}
		}

		allNode = {
			left: allNode,
			operator: 'AND',
			parenthesized: false,
			right: steelgradeCondition
		};

		return luceneToString(allNode);
	}

	batchesCustomerArticleFilter(contentType: string, contentValues: string[]) {
		if (contentValues.length === 0) {
			return '';
		}

		let allNode: FieldExpression | NodeExpression = {
			field: 'statusGroup',
			term: GROUP_STATUS_READY_TO_SHIP,
			quoted: true
		};
		let customerArticleReferenceCondition: LuceneExpression;
		if (contentValues.length === 1) {
			customerArticleReferenceCondition = {
				field: contentType,
				term: contentValues[0],
				quoted: true
			};
		} else {
			customerArticleReferenceCondition = {
				field: contentType,
				left: {
					field: '<implicit>',
					term: contentValues[0],
					quoted: true
				},
				operator: '<implicit>',
				right: {
					field: '<implicit>',
					term: contentValues[1],
					quoted: true
				},
				parenthesized: true
			};
			let current = customerArticleReferenceCondition;
			for (const customerArticleReference of contentValues.slice(2)) {
				current.right = {
					left: current.right,
					operator: '<implicit>',
					right: {
						field: '<implicit>',
						term: customerArticleReference,
						quoted: true
					},
					parenthesized: false
				};
				current = current.right;
			}
		}

		allNode = {
			left: allNode,
			operator: 'AND',
			parenthesized: false,
			right: customerArticleReferenceCondition
		};

		return luceneToString(allNode);
	}
}
